// src/types/brand.ts

export type BrandModeCreationType =
  | "campaign_post"
  | "banner"
  | "ad"
  | "product_creative";

export type BrandTone =
  | "professional"
  | "friendly"
  | "bold"
  | "luxury"
  | "playful"
  | "minimal";

export type BrandVisualStyle =
  | "photoreal"
  | "illustration"
  | "3d"
  | "minimal"
  | "futuristic"
  | "editorial"
  | "vintage";

export type BrandImageType =
  | "product"
  | "lifestyle"
  | "portrait"
  | "abstract"
  | "mockup";

export type BrandKit = {
  // ✅ Persistencia (fase 1): localStorage por usuario
  // Luego se puede llevar a Supabase (fase 2)
  brandName?: string;

  // ✅ Diferencial: consistencia
  colors: string[]; // hex: ["#111827", "#7C3AED", ...]
  visualStyle: BrandVisualStyle;
  tone: BrandTone;
  imageType: BrandImageType;

  // Extra útil (opcional):
  notes?: string; // "Evitar texto en imagen", "estilo Apple", etc.
};

/**
 * ✅ Advanced (solo Pro+)
 * - paleta/mood/referencia (lo que definiste)
 */
export type BrandAdvanced = {
  mood?: string; // "clean premium", "energetic", "lux editorial", etc.
  referenceUrl?: string; // link de referencia (opcional)
};

/**
 * ✅ Brand profile (una marca)
 * Free: puede tener name/description, pero NO kit persistente
 * Basic+: puede guardar kit
 * Pro+: puede guardar advanced
 */
export type BrandProfile = {
  id: string;
  name: string;
  description: string;

  // Solo se guarda si el plan lo permite
  kit?: BrandKit;

  // Solo Pro+
  advanced?: BrandAdvanced;

  createdAt: number;
  updatedAt: number;
};

/**
 * ✅ Store (multi-brand)
 */
export type BrandStore = {
  version: 1;
  activeBrandId: string | null;
  brands: BrandProfile[];
};

export type BrandPreset = {
  id: BrandModeCreationType;
  title: string;
  subtitle: string;

  // ✅ Para UI (icon emoji por ahora)
  icon: string;

  // ✅ Recomendación (más adelante puede influir en prompts)
  recommendedAspectRatio: "1:1" | "4:5" | "16:9" | "9:16";

  // ✅ Copys para guiar (sin prompt libre vacío)
  briefPlaceholders: {
    goal: string; // objetivo / CTA
    productOrTopic: string; // qué vendes / qué anuncias
    audience: string; // para quién
  };

  // ✅ Lista de entregables (pilar 1)
  outputsExamples: string[];
};

export type BrandCreateForm = {
  presetId: BrandModeCreationType;

  // Brief mínimo para guiar IA
  goal: string; // "Vender", "Captar leads", "Lanzamiento"
  productOrTopic: string; // "Proteína whey", "App fitness", etc.
  audience: string; // "Mujeres 25-35", "Empresas B2B", etc.

  // ⚡ Opcional: override del brand kit (si no quieren)
  // Si no se llena, usaremos el BrandKit guardado
  overrideKit?: Partial<BrandKit>;
};

// ─────────────────────────────────────────────
// LocalStorage helpers (fase 1)
// ─────────────────────────────────────────────

// Legacy (kit único)
export const BRAND_KIT_STORAGE_KEY_PREFIX = "ovylon_brand_kit_";

export function buildBrandKitStorageKey(userIdOrEmail: string) {
  const safe = String(userIdOrEmail || "").trim().toLowerCase();
  return `${BRAND_KIT_STORAGE_KEY_PREFIX}${safe}`;
}

export function defaultBrandKit(): BrandKit {
  return {
    brandName: "Mi marca",
    colors: ["#7C3AED", "#06B6D4", "#111827"], // purple, cyan, slate
    visualStyle: "editorial",
    tone: "professional",
    imageType: "lifestyle",
    notes: "",
  };
}

// ✅ Nuevo store (multi-brand)
export const BRAND_STORE_STORAGE_KEY_PREFIX = "ovylon_brand_store_";

export function buildBrandStoreStorageKey(userIdOrEmail: string) {
  const safe = String(userIdOrEmail || "").trim().toLowerCase();
  return `${BRAND_STORE_STORAGE_KEY_PREFIX}${safe}`;
}

function uid() {
  // suficiente para localStorage (no críptico)
  return `b_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

export function defaultBrandProfile(): BrandProfile {
  const now = Date.now();
  return {
    id: uid(),
    name: "Mi marca",
    description: "",
    kit: defaultBrandKit(),
    advanced: {},
    createdAt: now,
    updatedAt: now,
  };
}

export function defaultBrandStore(): BrandStore {
  const b = defaultBrandProfile();
  return {
    version: 1,
    activeBrandId: b.id,
    brands: [b],
  };
}

/**
 * ✅ Migración legacy -> store nuevo
 * - Si existía un BrandKit único guardado antes, lo metemos como brand #1
 */
export function migrateLegacyBrandKitToStore(legacyKit: BrandKit): BrandStore {
  const now = Date.now();
  const b: BrandProfile = {
    id: uid(),
    name: legacyKit.brandName || "Mi marca",
    description: "",
    kit: legacyKit,
    advanced: {},
    createdAt: now,
    updatedAt: now,
  };

  return {
    version: 1,
    activeBrandId: b.id,
    brands: [b],
  };
}

